import * as React from 'react'
import { Box, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { ContentCopy, ExpandMore, ExpandLess } from '@mui/icons-material'
import TreeActions from './Actions'

const DETAIL_COLUMN_SPAN = 9
const ICON_BUTTON_SIZE = 36
const TABLE_ICON_BUTTON_SX = {
  width: ICON_BUTTON_SIZE,
  height: ICON_BUTTON_SIZE,
}

const FIXED_COLUMN_WIDTHS = {
  select: 52,
  expand: 52,
  status: 124,
  actions: 112,
}

const FLEX_COLUMN_SIZES = {
  code: { flex: 0.9, minWidth: 120 },
  tenant: { flex: 1.35, minWidth: 180 },
  species: { flex: 1.45, minWidth: 190 },
  site: { flex: 1.65, minWidth: 200 },
  date: { flex: 0.75, minWidth: 112 },
}

const getTenantDisplayName = (row, tenantNamesById) => {
  const tenantId = row.tenantId == null ? null : String(row.tenantId)
  return (
    (tenantId && tenantNamesById?.[tenantId]) ||
    row.tenantNombreCompleto ||
    row.tenantFullName ||
    row.tenant?.fullName ||
    row.tenant?.nombre_completo ||
    row.tenantNombre ||
    '—'
  )
}

const WrapCellText = ({ value, align = 'left', italic = false, variant = 'body2' }) => (
  <Typography
    variant={variant}
    sx={{
      width: '100%',
      minWidth: 0,
      color: 'text.primary',
      fontStyle: italic ? 'italic' : 'normal',
      fontSize: variant === 'caption' ? 12 : 13.5,
      fontWeight: 400,
      lineHeight: 1.28,
      textAlign: align,
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    }}>
    {value || '—'}
  </Typography>
)

const formatDate = iso => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatDateTime = iso => {
  if (!iso) return 'Sin actualización'
  return new Date(iso).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatCoordinate = value => {
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) return null
  return numberValue.toFixed(6)
}

const getCoordinateValue = (coordinates, key) => {
  if (coordinates?.loading) return 'Cargando...'
  if (coordinates?.error) return 'No disponible'
  return formatCoordinate(coordinates?.[key]) || 'No disponible'
}

const DetailPill = ({ label, value, action }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: action ? 'auto minmax(0, 1fr) auto' : 'auto minmax(0, 1fr)',
      alignItems: 'center',
      columnGap: 0.85,
      minWidth: 0,
      px: 1,
      py: 0.625,
      border: '1px solid #D7E1EE',
      borderRadius: 1,
      bgcolor: '#FFFFFF',
    }}>
    <Typography
      variant="caption"
      sx={{
        color: 'primary.main',
        fontSize: { xs: 11, md: 12 },
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase',
      }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
        fontSize: { xs: 13, md: 14 },
        fontWeight: 400,
        overflowWrap: 'anywhere',
        lineHeight: 1.3,
      }}>
      {value || 'No registrado'}
    </Typography>
    {action}
  </Box>
)

const DetailTextBlock = ({ label, value }) => (
  <Box
    sx={{
      minWidth: 0,
      px: 1,
      py: 0.875,
      border: '1px solid #D7E1EE',
      borderRadius: 1,
      bgcolor: '#FFFFFF',
    }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 0.35,
        color: 'primary.main',
        fontSize: { xs: 11, md: 12 },
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase',
      }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
        fontSize: { xs: 13, md: 14 },
        fontWeight: 500,
        lineHeight: 1.45,
        overflowWrap: 'anywhere',
      }}>
      {value || 'No registrado'}
    </Typography>
  </Box>
)

const ExpandedDetail = ({ row, onCopyUuid }) => (
  <Box
    sx={{
      width: '100%',
      flex: '1 1 100%',
      alignSelf: 'stretch',
      boxSizing: 'border-box',
      minWidth: 0,
      py: 1,
      px: 1.25,
      bgcolor: '#F7FAFE',
      borderTop: '1px solid #D7E1EE',
      borderBottom: '1px solid #D7E1EE',
      borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
    }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
        mb: 0.875,
      }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: 'text.forestGreen',
            fontSize: { xs: 12, md: 13 },
            fontWeight: 650,
            lineHeight: 1.25,
          }}>
          DETALLE DE REGISTRO
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: '#476226', fontSize: { xs: 12, md: 13 }, lineHeight: 1.3 }}>
          Datos complementarios del árbol seleccionado
        </Typography>
      </Box>
      <Chip
        size="small"
        variant="outlined"
        label={
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              lineHeight: 1,
            }}>
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                fontWeight: 550,
                px: 0.5,
                fontSize: { md: 10, lg: 11, xl: 12 },
                textAlign: 'center',
                lineHeight: 1,
              }}>
              ACTUALIZADO:
            </Box>
            <Box
              component="span"
              sx={{ color: '#6F87A6', fontWeight: 400, lineHeight: 1 }}>
              {formatDateTime(row.fechaActualizacion)}
            </Box>
          </Box>
        }
        sx={{
          bgcolor: '#FFFFFF',
          borderColor: '#B9C8DC',
          color: 'inherit',
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: '1.4fr 0.7fr 0.7fr 0.9fr',
        },
        gap: 0.75,
        mb: 0.75,
      }}>
      <DetailPill
        label="UUID"
        value={row.uuid}
        action={
          <Tooltip title="Copiar UUID">
            <span>
              <IconButton
                size="small"
                disabled={!row.uuid}
                sx={TABLE_ICON_BUTTON_SX}
                onClick={e => {
                  e.stopPropagation()
                  onCopyUuid(row.uuid)
                }}>
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <DetailPill label="Latitud" value={getCoordinateValue(row.coordinates, 'lat')} />
      <DetailPill label="Longitud" value={getCoordinateValue(row.coordinates, 'lng')} />
      <DetailPill label="Colector" value={row.colectorRegistro || 'No registrado'} />
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 0.75,
      }}>
      <DetailTextBlock
        label="Referencia de ubicación"
        value={row.referencia_ubicacion || 'Sin referencia'}
      />
      <DetailTextBlock
        label="Observación"
        value={row.comentario || 'Sin observaciones'}
      />
    </Box>
  </Box>
)

export const getColumns = ({
  expandedRows,
  onToggleExpand,
  onCopyUuid,
  onCopyCode,
  tenantNamesById,
  selectedTreeIds,
  onToggleSelect,
  onToggleSelectVisible,
  visibleSelectionState,
  onRequestDelete,
}) => [
  {
    field: '_select',
    headerName: '',
    width: FIXED_COLUMN_WIDTHS.select,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    colSpan: params => (params.row._isDetail ? DETAIL_COLUMN_SPAN : 1),
    renderHeader: () => (
      <Tooltip title="Seleccionar registros visibles">
        <Checkbox
          size="small"
          checked={visibleSelectionState.all}
          indeterminate={visibleSelectionState.some}
          disabled={!visibleSelectionState.total}
          onChange={onToggleSelectVisible}
        />
      </Tooltip>
    ),
    renderCell: params => {
      if (params.row._isDetail) return <ExpandedDetail row={params.row} onCopyUuid={onCopyUuid} />
      const uuid = params.row.uuid
      return (
        <Checkbox
          size="small"
          checked={selectedTreeIds.has(uuid)}
          onClick={event => event.stopPropagation()}
          onChange={() => onToggleSelect(uuid)}
        />
      )
    },
  },
  {
    field: '_expand',
    headerName: '',
    width: FIXED_COLUMN_WIDTHS.expand,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => {
      if (params.row._isDetail) return null
      const isExpanded = expandedRows.has(params.row.uuid)
      return (
        <IconButton
          size="small"
          sx={TABLE_ICON_BUTTON_SX}
          onClick={() => onToggleExpand(params.row.uuid)}>
          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      )
    },
  },
  {
    field: 'codigo',
    headerName: 'Código',
    headerAlign: 'center',
    align: 'center',
    ...FLEX_COLUMN_SIZES.code,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      const code = params.value
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            minWidth: 0,
            width: '100%',
          }}>
          <WrapCellText value={code} align="center" />
          <Tooltip title="Copiar código">
            <span>
              <IconButton
                size="small"
                disabled={!code}
                sx={TABLE_ICON_BUTTON_SX}
                onClick={event => {
                  event.stopPropagation()
                  onCopyCode(code)
                }}>
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )
    },
  },
  {
    field: 'tenantNombre',
    headerName: 'Tenant',
    headerAlign: 'center',
    align: 'center',
    ...FLEX_COLUMN_SIZES.tenant,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      return (
        <WrapCellText
          value={getTenantDisplayName(params.row, tenantNamesById)}
          align="center"
        />
      )
    },
  },
  {
    field: 'especieNombre',
    headerName: 'Especie',
    headerAlign: 'center',
    ...FLEX_COLUMN_SIZES.species,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      return <WrapCellText value={params.value} italic />
    },
  },
  {
    field: 'sitioNombre',
    headerName: 'Sitio',
    headerAlign: 'center',
    ...FLEX_COLUMN_SIZES.site,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      return <WrapCellText value={params.value} />
    },
  },
  {
    field: 'fechaRegistro',
    headerName: 'Registro',
    headerAlign: 'center',
    align: 'center',
    ...FLEX_COLUMN_SIZES.date,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      return (
        <WrapCellText value={formatDate(params.value)} align="center" variant="caption" />
      )
    },
  },
  {
    field: 'habilitado',
    headerName: 'Estado',
    headerAlign: 'center',
    align: 'center',
    width: FIXED_COLUMN_WIDTHS.status,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      return (
        <Chip
          label={params.value ? 'Habilitado' : 'Deshabilitado'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
  },
  {
    field: 'correccionAplicadaAt',
    headerName: 'Corrección',
    headerAlign: 'center',
    align: 'center',
    ...FLEX_COLUMN_SIZES.date,
    sortable: false,
    renderCell: params => {
      if (params.row._isDetail) return null
      if (!params.value) return null
      const nombre = params.row.correccionAplicadaPorNombre
      return (
        <WrapCellText
          value={`Aplicada ${formatDate(params.value)}${nombre ? ` · ${nombre}` : ''}`}
          align="center"
          variant="caption"
        />
      )
    },
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    headerAlign: 'center',
    align: 'center',
    width: FIXED_COLUMN_WIDTHS.actions,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => {
      if (params.row._isDetail) return null
      return <TreeActions tree={params.row} onRequestDelete={onRequestDelete} />
    },
  },
]
